// Liste mit Icons statt <select> (Handoff §4): klappt im Feld auf, gewählte Zeile mit Haken.
// Tastatur: Enter/Leertaste/Pfeil öffnet, Pfeile wandern, Enter wählt, Escape schließt NUR die Liste.
// Optional: Suchfeld oben in der Liste (Filter liegt im Formular), Gruppenköpfe (wie <optgroup>),
// eine Zusatzzeile (z. B. „Auswahl entfernen“) und ein Leer-Text – alles nur mit Texten aus dem Code.
import { Fragment, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { CaretDown, Check, MagnifyingGlass, type Icon } from '@phosphor-icons/react';
import { FeldHuelle } from './FormFeld';
import { AUSWAHL_KOMPAKT, FELD, FORM } from './form-stil';

/** Platz unter einer aufgeklappten Liste beim Hineinscrollen: Höhe der festen Knopfleiste + Luft. */
export const LISTE_ABSTAND_UNTEN = 96;

export interface FormAuswahlOption<K extends string> {
  wert: K;
  label: string;
  /** Zweite Zeile – nur mit Text aus dem Code. */
  unter?: string;
  icon?: Icon;
  iconFarbe?: string;
  iconFlaeche?: string;
  /** Gruppenkopf (wie <optgroup label>) – aufeinanderfolgende Optionen mit gleicher Gruppe stehen darunter. */
  gruppe?: string;
}

export interface FormAuswahlProps<K extends string> {
  label: string;
  optionen: readonly FormAuswahlOption<K>[];
  /** null = nichts gewählt (dann Platzhalter). */
  wert: K | null;
  onChange: (wert: K) => void;
  /** Anzeige, wenn nichts gewählt ist (Text aus dem Code). */
  platzhalter?: string;
  /** Anzeige im geschlossenen Feld, falls sie vom Optionslabel abweicht. */
  anzeige?: string;
  /** Icon zur Anzeige, wenn keine Option gewählt ist bzw. `anzeige` gesetzt ist. */
  anzeigeIcon?: Icon;
  pflicht?: boolean;
  fehler?: boolean;
  tip?: string;
  /** Kleine Chip-Variante ohne Feldhülle (z. B. in Tabellenzeilen); `label` dient dann nur Screenreadern. */
  kompakt?: boolean;
  hinweis?: ReactNode;
  /** Suchfeld oben in der geöffneten Liste; gefiltert wird im Formular. */
  suchfeld?: { wert: string; onChange: (wert: string) => void; platzhalter: string };
  /** Text, wenn die Liste leer ist (aus dem Code). */
  leerText?: string;
  /** Zusätzliche Zeile über den Optionen (z. B. „Auswahl entfernen“). */
  zusatzZeile?: { label: string; icon?: Icon; onWaehlen: () => void };
}

export function FormAuswahl<K extends string>({
  label, optionen, wert, onChange, platzhalter, anzeige, anzeigeIcon, pflicht, fehler, tip, kompakt, hinweis, suchfeld, leerText, zusatzZeile,
}: FormAuswahlProps<K>) {
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState(0);
  const id = useId();
  const listeRef = useRef<HTMLDivElement>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);
  // Aufgeklappte Liste ganz sichtbar machen (nicht hinter der Knopfleiste).
  useEffect(() => { if (offen) listeRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [offen]);
  const gewaehlt = optionen.find(o => o.wert === wert) ?? null;
  const text = anzeige ?? gewaehlt?.label ?? platzhalter ?? '';
  const I = gewaehlt?.icon ?? anzeigeIcon;

  const oeffnen = () => {
    setAktiv(Math.max(0, optionen.findIndex(o => o.wert === wert)));
    setOffen(true);
  };
  const waehlen = (o: FormAuswahlOption<K>) => { onChange(o.wert); setOffen(false); knopfRef.current?.focus(); };
  const tastatur = (e: KeyboardEvent<HTMLElement>) => {
    if (!offen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); oeffnen(); }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOffen(false); knopfRef.current?.focus(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setAktiv(i => Math.min(optionen.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setAktiv(i => Math.max(0, i - 1)); }
    // Leertaste wählt nur im Knopf – im Suchfeld ist sie ein Zeichen.
    if (e.key === 'Enter' || (e.key === ' ' && e.currentTarget === knopfRef.current)) {
      e.preventDefault();
      const o = optionen[aktiv];
      if (o) waehlen(o);
    }
  };
  // Fokus verlässt den ganzen Baustein (Knopf + Liste + Suchfeld) → Liste zu (wie „Klick daneben“).
  const raus = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOffen(false);
  };

  const inhalt = (
    <div className="flex min-w-0 flex-col" onBlur={raus}>
      <button ref={knopfRef} type="button" onClick={() => (offen ? setOffen(false) : oeffnen())} onKeyDown={tastatur}
        aria-haspopup="listbox" aria-expanded={offen} aria-controls={offen ? `${id}-liste` : undefined}
        aria-activedescendant={offen ? `${id}-${aktiv}` : undefined} aria-label={`${label}: ${text}`}
        data-tip={kompakt ? tip : undefined}
        className={kompakt ? 'flex min-w-0 max-w-full cursor-pointer items-center self-start border-none text-left' : 'pf-ausloeser flex w-full cursor-pointer items-center border-none bg-transparent p-0 text-left'}
        style={kompakt
          ? { gap: 6, padding: '4px 6px 4px 10px', borderRadius: 999, font: 'inherit', background: AUSWAHL_KOMPAKT.flaeche, boxShadow: AUSWAHL_KOMPAKT.ring }
          : { gap: 8, minHeight: 24, font: 'inherit' }}>
        {I && <I aria-hidden size={14} weight="fill" color={gewaehlt?.iconFarbe ?? 'rgba(255,255,255,0.5)'} className="shrink-0" />}
        <span className="min-w-0 flex-1 truncate" style={kompakt
          ? { fontSize: 12, fontWeight: 800, color: FORM.gold }
          : { fontSize: 15, fontWeight: 600, color: gewaehlt || anzeige ? FORM.text : 'rgba(255,255,255,0.4)' }}>{text}</span>
        <span aria-hidden className="flex shrink-0 items-center justify-center rounded-full" style={{
          width: kompakt ? 18 : 24, height: kompakt ? 18 : 24, background: 'rgba(255,255,255,0.08)',
          transform: offen ? 'rotate(180deg)' : 'none', transition: 'transform .3s cubic-bezier(.32,.72,0,1)',
        }}>
          <CaretDown size={11} weight="bold" color="rgba(255,255,255,0.7)" />
        </span>
      </button>
      {offen && (
        <div ref={listeRef} className="pf-drop flex flex-col overflow-hidden"
          style={{ scrollMarginBottom: LISTE_ABSTAND_UNTEN, margin: kompakt ? '6px 0 0' : '6px -6px 0', borderRadius: 13, background: FELD.liste, boxShadow: FELD.listeRing }}>
          {suchfeld && (
            <label className="flex items-center" style={{ gap: 8, padding: '8px 12px', borderBottom: `0.5px solid ${FORM.linie}` }}>
              <MagnifyingGlass aria-hidden size={14} weight="bold" color="rgba(255,255,255,0.35)" className="shrink-0" />
              <input autoFocus value={suchfeld.wert} onChange={(e) => suchfeld.onChange(e.target.value)} onKeyDown={tastatur}
                placeholder={suchfeld.platzhalter} aria-label={suchfeld.platzhalter} aria-controls={`${id}-liste`}
                className="pf-klein min-w-0 flex-1 border-none bg-transparent outline-none"
                style={{ font: 'inherit', fontSize: 14, fontWeight: 600, color: FORM.text, boxShadow: 'none', borderRadius: 8, padding: '2px 6px' }} />
            </label>
          )}
          <div id={`${id}-liste`} role="listbox" aria-label={label} className="pf-scroll overflow-y-auto" style={{ maxHeight: 208, padding: 4 }}>
            {zusatzZeile && (
              <OptionZeile id={`${id}-zusatz`} option={{ label: zusatzZeile.label, icon: zusatzZeile.icon }} an={false} aktiv={false}
                onWaehlen={() => { zusatzZeile.onWaehlen(); setOffen(false); }} />
            )}
            {optionen.map((o, i) => (
              <Fragment key={o.wert}>
                {o.gruppe && o.gruppe !== optionen[i - 1]?.gruppe && (
                  <div role="presentation" style={{ padding: '8px 10px 4px', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>{o.gruppe}</div>
                )}
                <OptionZeile id={`${id}-${i}`} option={o} an={o.wert === wert} aktiv={i === aktiv} onWaehlen={() => waehlen(o)} />
              </Fragment>
            ))}
            {optionen.length === 0 && leerText && (
              <div style={{ padding: 14, textAlign: 'center', fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>{leerText}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
  return kompakt
    ? inhalt
    : <FeldHuelle label={label} pflicht={pflicht} fehler={fehler} tip={tip} hinweis={hinweis}>{inhalt}</FeldHuelle>;
}

/** Zeile einer aufgeklappten Liste (auch von FormSuche genutzt). */
export function OptionZeile({ id, option, an, aktiv, onWaehlen }: {
  id: string;
  option: { label: string; unter?: string; icon?: Icon; iconFarbe?: string; iconFlaeche?: string };
  an: boolean;
  aktiv: boolean;
  onWaehlen: () => void;
}) {
  const I = option.icon;
  return (
    <div id={id} role="option" aria-selected={an} data-aktiv={aktiv}
      // onMouseDown statt onClick: wählt, bevor das Feld den Fokus verliert.
      onMouseDown={(e) => { e.preventDefault(); onWaehlen(); }}
      className="pf-zeile flex cursor-pointer items-center"
      style={{ gap: 10, padding: '8px 10px', borderRadius: 10, background: an ? FELD.zeileAktiv : 'transparent' }}>
      {I && (
        <span aria-hidden className="flex shrink-0 items-center justify-center" style={{
          width: 28, height: 28, borderRadius: 9, background: option.iconFlaeche ?? 'rgba(255,255,255,0.1)',
        }}>
          <I size={14} weight="fill" color={option.iconFarbe ?? '#fff'} />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700, color: FORM.text }}>{option.label}</span>
        {option.unter && <span className="truncate" style={{ fontSize: 11.5, fontWeight: 600, color: FORM.text3 }}>{option.unter}</span>}
      </span>
      {an && <Check aria-hidden size={14} weight="bold" color={FORM.gold} className="shrink-0" />}
    </div>
  );
}
